import { UserIdentityProvider } from "@core/services/user-identity.context";
import Billing from "@domain/billing/billing";
import EditBilling from "@domain/billing/edit-billing/edit-billing";
import ListBilling from "@domain/billing/list-billing/list-billing";
import NewBilling from "@domain/billing/new-billing/new-billing";
import BillingContract from "@domain/contract/billing-contract/billing-contract";
import Contract from "@domain/contract/contract";
import EditContract from "@domain/contract/edit-contract/edit-contract";
import FormContract from "@domain/contract/form-contract/form-contract";
import ListContract from "@domain/contract/list-contract/list-contract";
import NewContract from "@domain/contract/new-contract/new-contract";
import PayContract from "@domain/contract/pay-contract/pay-contract";
import Dashboard from "@domain/dashboard/dashboard";
import Login from "@domain/login/login";
import Page404 from "@domain/page-404/page-404";
import RouteErrorBoundary from "@domain/page-404/route-error-boundary";
import EditPatient from "@domain/patient/edit-patient/edit-patient";
import FormPatient from "@domain/patient/form-patient/form-patient";
import ListPatient from "@domain/patient/list-patient/list-patient";
import NewPatient from "@domain/patient/new-patient/new-patient";
import NewPatientVisit from "@domain/patient/new-patient-visit/new-patient-visit";
import Patient from "@domain/patient/patient";
import EditPayment from "@domain/payment/edit-payment/edit-payment";
import ListPayment from "@domain/payment/list-payment/list-payment";
import NewPayment from "@domain/payment/new-payment/new-payment";
import Payment from "@domain/payment/payment";
import EditPerson from "@domain/person/edit-person/edit-person";
import ListPerson from "@domain/person/list-person/list-person";
import NewPerson from "@domain/person/new-person/new-person";
import Person from "@domain/person/person";
import EditProviderType from "@domain/provider-type/edit-provider-type/edit-provider-type";
import ListProviderType from "@domain/provider-type/list-provider-type/list-provider-type";
import NewProviderType from "@domain/provider-type/new-provider-type/new-provider-type";
import ProviderType from "@domain/provider-type/provider-type";
import EditRegistration from "@domain/registration/edit-registration/edit-registration";
import { FilterRegistrationProvider } from "@domain/registration/filter-registration/filter-registration.context";
import ListRegistration from "@domain/registration/list-registration/list-registration";
import NewRegistration from "@domain/registration/new-registration/new-registration";
import Registration from "@domain/registration/registration";
import EditRole from "@domain/role/edit-role/edit-role";
import ListRole from "@domain/role/list-role/list-role";
import NewRole from "@domain/role/new-role/new-role";
import Role from "@domain/role/role";
import AuthShell from "@domain/shell/auth-shell/auth-shell";
import MainShell from "@domain/shell/main-shell/main-shell";
import EditTenant from "@domain/tenant/edit-tenant/edit-tenant";
import ListTenant from "@domain/tenant/list-tenant/list-tenant";
import NewTenant from "@domain/tenant/new-tenant/new-tenant";
import Tenant from "@domain/tenant/tenant";
import EditUtilService from "@domain/util-service/edit-util-service/edit-util-service";
import ListUtilService from "@domain/util-service/list-util-service/list-util-service";
import NewUtilService from "@domain/util-service/new-util-service/new-util-service";
import UtilService from "@domain/util-service/util-service";
import EditVisit from "@domain/visit/edit-visit/edit-visit";
import ListVisit from "@domain/visit/list-visit/list-visit";
import NewVisit from "@domain/visit/new-visit/new-visit";
import Visit from "@domain/visit/visit";
import EditWaiver from "@domain/waiver/edit-waiver/edit-waiver";
import ListWaiver from "@domain/waiver/list-waiver/list-waiver";
import NewWaiver from "@domain/waiver/new-waiver/new-waiver";
import Waiver from "@domain/waiver/waiver";
import { SessionProvider } from "@factory/authentication/session.context";
import { ProtectedRoute } from "@shared/ui/protected-route/protected-route";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";

const tenant = {
  path: "tenant",
  element: <Tenant />,
  children: [
    {
      path: "",
      element: <ListTenant />,
    },
    {
      path: "new",
      element: <NewTenant />,
    },
    {
      path: ":tenantId",
      element: <EditTenant />,
    },
  ],
};
const role = {
  path: "role",
  element: <Role />,
  children: [
    {
      path: "",
      element: <ListRole />,
    },
    {
      path: "new",
      element: <NewRole />,
    },
    {
      path: ":roleId",
      element: <EditRole />,
    },
  ],
};
const account = {
  path: "account",
  element: <Person />,
  children: [
    {
      path: "",
      element: <ListPerson />,
    },
    {
      path: "new",
      element: <NewPerson />,
    },
    {
      path: ":personId",
      element: <EditPerson />,
    },
  ],
};
const visit = {
  path: "visit",
  element: <Visit />,
  children: [
    {
      path: "",
      element: <ListVisit />,
    },
    {
      path: "new",
      element: <NewVisit />,
    },
    {
      path: ":visitId",
      element: <EditVisit />,
    },
  ],
};
const payment = {
  path: "payment",
  element: <Payment />,
  children: [
    {
      path: "",
      element: <ListPayment />,
    },
    {
      path: "new",
      element: <NewPayment />,
    },
    {
      path: ":paymentId",
      element: <EditPayment />,
    },
  ],
};
const contract = {
  path: "contract",
  element: <Contract />,
  children: [
    {
      path: "",
      element: <ListContract />,
    },
    {
      path: "new",
      element: <NewContract />,
    },
    {
      path: ":contractId",
      element: <EditContract />,
      children: [
        { path: "", element: <FormContract isNew={false} /> },
        { path: "pay-session", element: <PayContract /> },
        { path: "pay", element: <PayContract isBulk={true} /> },
        { path: "billing", element: <BillingContract /> },
        payment,
        visit,
      ],
    },
  ],
};
const patient = {
  path: "patient",
  element: <Patient />,
  children: [
    {
      path: "",
      element: <ListPatient />,
      children: [{ path: "visit", element: <NewPatientVisit /> }],
    },
    {
      path: "new",
      element: <NewPatient />,
    },
    {
      path: ":patientId",
      element: <EditPatient />,
      children: [
        {
          path: "",
          element: <FormPatient />,
        },
        visit,
        contract,
      ],
    },
  ],
};
const utilService = {
  path: "util-service",
  element: <UtilService />,
  children: [
    {
      path: "",
      element: <ListUtilService />,
    },
    {
      path: "new",
      element: <NewUtilService />,
    },
    {
      path: ":utilServiceId",
      element: <EditUtilService />,
    },
  ],
};
const billing = {
  path: "billing",
  element: <Billing />,
  children: [
    {
      path: "",
      element: <ListBilling />,
    },
    {
      path: "new",
      element: <NewBilling />,
    },
    {
      path: ":billingId",
      element: <EditBilling />,
    },
  ],
};

const providerType = {
  path: "provider-type",
  element: <ProviderType />,
  children: [
    {
      path: "",
      element: <ListProviderType />,
    },
    {
      path: "new",
      element: <NewProviderType />,
    },
    {
      path: ":providerTypeId",
      element: <EditProviderType />,
    },
  ],
};

const waiver = {
  path: "waiver",
  element: <Waiver />,
  children: [
    {
      path: "",
      element: <ListWaiver />,
    },
    {
      path: "new",
      element: <NewWaiver />,
    },
    {
      path: ":waiverId",
      element: <EditWaiver />,
    },
  ],
};
const registration = {
  path: "registration",
  element: <Registration />,
  children: [
    {
      path: "",
      element: <ListRegistration />,
    },
    {
      path: "new",
      element: <NewRegistration />,
    },
    {
      path: ":registrationId",
      element: <EditRegistration />,
    },
  ],
};
// ROUTE DEFINITION

const router = createBrowserRouter([
  {
    path: "/",
    element: <MainShell />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <Navigate to="/login" replace /> },
      { path: "login", element: <Login /> },
      {
        path: "public/:client",
        children: [
          {
            path: "",
            element: (
              <FilterRegistrationProvider>
                <NewRegistration />
              </FilterRegistrationProvider>
            ),
          },
        ],
      },
      {
        path: ":client",
        element: (
          <UserIdentityProvider>
            <SessionProvider>
              <ProtectedRoute>
                <AuthShell />
              </ProtectedRoute>
            </SessionProvider>
          </UserIdentityProvider>
        ),
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            path: "",
            element: <Dashboard />,
          },
          tenant,
          role,
          account,
          patient,
          utilService,
          contract,
          billing,
          providerType,
          payment,
          visit,
          waiver,
          registration,
          // ROUTE ADD
        ],
      },
      { path: "404", element: <Page404 /> },
      { path: "*", element: <Navigate to="/404" replace /> },
    ],
  },
]);
export function AppRoute() {
  return <RouterProvider router={router} />;
}
